/**
 * frontend/src/modules/performance/hooks/useGpsDispersion.ts
 * P9 P7 G1 — Dispersion GPS begin↔PDV (chart 1).
 *
 * Paramètre :
 *   - merchCode présent → distribution DE ce merch (drawer)
 *   - merchCode absent  → distribution ÉQUIPE  (page module)
 *
 * Fenêtre : `latest_week` — doctrine GEO (DECISIONS D-GEO-FENETRE-DERNIERE-SEMAINE).
 * TOUS les charts géo-intégrité affichent la DERNIÈRE semaine importée (MAX de la
 * base via getLatestWeekMeta côté back), PAS 4 sem : le contrôle d'intégrité GPS
 * (dispersion, déplacement, fraude tournée) se juge à chaud sur la semaine récente
 * — une fraude ponctuelle ne doit pas être lissée. Distinct des feux performance
 * (4 sem pour la stabilité).
 *
 * Source : /api/v1/performance/kpis/gps-dispersion (vue partagée v_visit_gps_distance).
 */

import { useQuery } from '@tanstack/react-query';
import { fetchPerfKpi } from '../lib/api';
import type { PerfApiResponse, GpsDispersionData } from '../types/performance.types';

const STALE = 5 * 60_000;

/**
 * @param pdvType — canal du merch (`merchandiser.channel_type`, ancrage #327).
 *   Omis → le backend défaute à `pdv_type='all'` et n'applique AUCUN filtre canal
 *   (api/service.ts:200 : 'all' → undefined), donc retail + wholesale sont
 *   agrégés. Acceptable pour un merch précis (son canal est implicite) ou pour
 *   une vue équipe volontairement globale ; à proscrire pour un cadran censé
 *   parler d'UNE équipe. Retail et wholesale = 2 équipes distinctes.
 */
export function useGpsDispersion(merchCode?: string | null, pdvType?: 'retail' | 'wholesale') {
  return useQuery({
    // ⚠ pdvType DOIT être dans la clé : sinon la sidebar (retail) et la section
    // module (tous canaux) se partageraient la même entrée de cache.
    queryKey: ['perf-p9', 'gps-dispersion', merchCode ?? '__team__', pdvType ?? '__all__'] as const,
    queryFn:  () => fetchPerfKpi<GpsDispersionData>('gps-dispersion', {
      period_preset: 'latest_week',
      ...(merchCode ? { merch_code: merchCode } : {}),
      ...(pdvType   ? { pdv_type: pdvType }     : {}),
    }),
    staleTime: STALE,
    retry: 1,
    select: (r: PerfApiResponse<GpsDispersionData>) => r.data,
  });
}
